import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ErrorWithData} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {buildContactDisplayName} from '../../../../../helpers/models/contactHelpers';
import {HighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {shrinkFlexItemFirst, shrinkFlexItemLast} from '../../../../common/formatters/senders/common';
import {TeammatesFormatter} from '../../../../common/formatters/teammatesFormatter';
import {Highlight} from '../../header/search/highlight/highlight';

/*
 * Props.
 */

export interface CellRecipientTeammatesProps {
  conversation: ConversationBaseModel;
  trackerTeammates: ReadonlyArray<TeammateModel>;
  lastCommentAuthor?: TeammateModel;
}

interface CellRecipientProps extends CellRecipientTeammatesProps {
  recipientHighlight?: HighlightModel;
  recipient?: VisualRecipientModel;
  assignee?: TeammateModel;
  inbox?: InboxModel;
  isSelected?: boolean;
  isUnread?: boolean;
  userId?: number;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

interface CellRecipientStyleProps {
  $isSelected: boolean;
  $isUnread: boolean;
  $hasHighlight?: boolean;
}
const StyledRecipientDiv = styled.div<CellRecipientStyleProps>`
  ${shrinkFlexItemLast()}

  font-weight: ${(p) => (p.$isUnread ? fontWeights.bold : fontWeights.semibold)};

  /** If we have a highlight, rely on <Highlight /> to use the correct colors. */
  color: ${(p) => (p.$isSelected && !p.$hasHighlight ? p.theme.invertedText : p.theme.greys.shade90)};

  padding: 3px;
  margin: -3px;
  ${ellipsis()};
`;

export const StyledAssigneeChevronDiv = styled.div<CellRecipientStyleProps>`
  flex-shrink: 0;
  color: ${(p) => (p.$isSelected ? p.theme.invertedSubtext : p.theme.greys.shade50)};
`;

export const StyledAssigneeBaseDiv = styled.div<CellRecipientStyleProps>`
  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.interactiveText)};
  font-weight: ${(p) => (p.$isUnread ? fontWeights.bold : fontWeights.semibold)};
`;

const StyledAssigneeDiv = styled(StyledAssigneeBaseDiv)`
  ${shrinkFlexItemFirst()}

  ${ellipsis()};
`;

/*
 * Component.
 */

export const CellRecipient: FC<CellRecipientProps> = memo((props) => {
  const {recipient, inbox, recipientHighlight, isSelected} = props;

  // If a recipient was provided, render its name. If we have an inbox instead, render its name.
  const computedRecipient: string | undefined = isDefined(recipient)
    ? buildContactDisplayName(recipient)
    : inbox?.name;

  return (
    <StyledWrapperDiv>
      <StyledRecipientDiv
        $isSelected={Boolean(props.isSelected)}
        $isUnread={Boolean(props.isUnread)}
        $hasHighlight={hasRecipientHighlight(computedRecipient, recipientHighlight)}
      >
        {computedRecipient
          ? renderRecipientHighlight(computedRecipient, recipientHighlight, isSelected)
          : renderCellRecipientTeammates(props)}
      </StyledRecipientDiv>
      {maybeRenderAssignee(props)}
    </StyledWrapperDiv>
  );
});

function hasRecipientHighlight(
  computedRecipient?: string,
  highlight?: HighlightModel,
): highlight is HighlightModel {
  return Boolean(highlight && highlight.text === computedRecipient);
}

function renderRecipientHighlight(
  computedRecipient: string,
  highlight?: HighlightModel,
  isSelected?: boolean,
) {
  if (hasRecipientHighlight(computedRecipient, highlight)) {
    return (
      <Highlight {...highlight} backgroundColor={PaletteColorsEnum.YELLOW} isParentSelected={isSelected} />
    );
  }

  return computedRecipient;
}

export function renderCellRecipientTeammates(props: CellRecipientTeammatesProps) {
  const teammates = computeCellRecipientTeammates(props);

  return <TeammatesFormatter teammates={teammates} limit={3} showFirstNameOnly />;
}

function maybeRenderAssignee(props: CellRecipientProps) {
  if (!props.assignee) {
    return null;
  }

  return (
    <>
      <StyledAssigneeChevronDiv $isSelected={Boolean(props.isSelected)} $isUnread={Boolean(props.isUnread)}>
        <Icon name="chevronRightSmall" />
      </StyledAssigneeChevronDiv>
      <StyledAssigneeDiv $isSelected={Boolean(props.isSelected)} $isUnread={Boolean(props.isUnread)}>
        {props.userId === props.assignee.id ? (
          <Trans id="assignee-me">Me</Trans>
        ) : (
          buildTeammateDisplayName(props.assignee)
        )}
      </StyledAssigneeDiv>
    </>
  );
}

function computeCellRecipientTeammates(props: CellRecipientTeammatesProps) {
  // If trackers exist, render the name(s) of the tracker(s).
  if (props.trackerTeammates?.length > 0) {
    return props.trackerTeammates;
  }

  // Fall back to the author of the last comment.
  // E.g. when a bot starts a discussion with a shared inbox, there are no visible trackers
  // (bot trackers are filtered out by the backend).
  if (props.lastCommentAuthor) {
    return [props.lastCommentAuthor];
  }

  // The cell will not be able to render any author name.
  // No known use cases. If this is reached, consider adding another fallback above.
  reportToBugsnag(
    new ErrorWithData('Cannot determine cell recipient teammates', {conversation_id: props.conversation.id}),
  );
  return [];
}
